import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ShieldCheck, ChevronRight } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { isKRRole, isAgencyPrincipal } from '@/lib/utils';
import { agencyAPI } from '@/lib/api';
import {
  CommunicationPolicyBadge,
  COMMUNICATION_SOURCE_LABELS,
} from '@/components/common/CommunicationPolicyBadge';

/**
 * Compact governance-awareness card for an agency: current policy + counts
 * of active agent exceptions and restricted-communication agents. Read-only,
 * visibility-gated (KR + Agency Principal — Agency User + others never see
 * the card because the backend returns 403).
 */
export const AgencyCommunicationSummary = ({ agencyId }) => {
  const { user } = useAuth();
  const canView = isKRRole(user) || isAgencyPrincipal(user);
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!canView || !agencyId) { setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    setError(false);
    agencyAPI.getCommunicationSummary(agencyId)
      .then((res) => { if (!cancelled) setState(res.data); })
      .catch(() => { if (!cancelled) setError(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [agencyId, canView]);

  if (!canView) return null;
  if (error) return null;

  return (
    <Card data-testid="agency-communication-summary-block">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <ShieldCheck size={16} className="text-slate-500" />
            <CardTitle className="text-sm font-semibold text-slate-900">
              Communication Governance
            </CardTitle>
          </div>
          <Link
            to="/governance/communication-policy"
            className="text-xs text-sky-600 hover:text-sky-700 inline-flex items-center gap-1"
            data-testid="agency-communication-summary-link"
          >
            Governance Inbox <ChevronRight size={12} />
          </Link>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-sm text-slate-500" data-testid="agency-communication-summary-loading">
            Loading governance summary…
          </p>
        ) : state ? (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="space-y-1" data-testid="agency-comm-current-policy">
              <span className="text-[11px] uppercase tracking-wider text-slate-500">Current Agency Policy</span>
              <div className="flex items-center gap-2">
                <CommunicationPolicyBadge value={state.agency_policy} />
              </div>
              <p className="text-[11px] text-slate-500">
                {COMMUNICATION_SOURCE_LABELS[state.agency_policy_source] || state.agency_policy_source}
              </p>
            </div>
            <div className="space-y-1" data-testid="agency-comm-exceptions-count">
              <span className="text-[11px] uppercase tracking-wider text-slate-500">Active Agent Exceptions</span>
              <div className="text-2xl font-semibold text-slate-900">
                {state.active_agent_exceptions_count}
              </div>
              <p className="text-[11px] text-slate-500">
                {state.active_agent_exceptions_count === 1
                  ? '1 agent has an active exception'
                  : `${state.active_agent_exceptions_count} agents have active exceptions`}
              </p>
            </div>
            <div className="space-y-1" data-testid="agency-comm-restricted-count">
              <span className="text-[11px] uppercase tracking-wider text-slate-500">Restricted Communication</span>
              <div className="text-2xl font-semibold text-slate-900">
                {state.restricted_agents_count}
              </div>
              <p className="text-[11px] text-slate-500">
                out of {state.total_agents} agents
                {state.pending_agent_requests_count > 0
                  ? ` · ${state.pending_agent_requests_count} pending`
                  : ''}
              </p>
            </div>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
};

export default AgencyCommunicationSummary;
